import { QueryOptions } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { NextPageContextApp, PageErrorType } from 'listing-details/types';
import { fetchListingRedirect, handleListingRedirect } from '.';

const getListingPageData = async (
    { res, query, apolloClient }: NextPageContextApp,
    listingDataQuery: QueryOptions,
    path: string
) => {
    const listingIdParam = query['listingId'] as string;
    const listingId = parseInt(listingIdParam, 10);

    let errorCode: PageErrorType;
    let data = null;

    if (!apolloClient) {
        errorCode = 500;
        if (res) {
            res.statusCode = errorCode;
        }
        return { errorCode, listingId };
    }

    // Ensure ID is numeric and smaller than 32 bit
    if (!listingIdParam.match(/^[0-9]+$/) || listingId > Math.pow(2, 31)) {
        errorCode = 404;
        if (res) {
            res.statusCode = errorCode;
        }
        return { errorCode, listingId };
    }

    // Check for valid listing (and correct path) before rendering the page
    // itself so we can short circuit to either a redirect to the correct
    // path or an error page with appropriate status code (this is
    // particularly important in the SSR context)
    try {
        data = await apolloClient.query(listingDataQuery);
    } catch (e) {
        // A GraphQL error occurs as a result of executing a valid query that returned abnormal
        // results
        const graphQLErrors = e.graphQLErrors.map(
            (error: GraphQLError) => error.extensions?.status
        );

        const isNetworkError = Boolean(e.networkError);
        const isServerError = graphQLErrors.includes(500);

        // Assume unrecoverable server error as default case
        errorCode = 500;

        // A network error is either literally a network error (i.e. cannot reach the server)
        // or an internal GraphQL error caused by a malformed query etc. Fundamentally it means
        // the query was not executed - we cannot recover from this but it's likely temporary
        if (isNetworkError || isServerError) {
            errorCode = 500;
        } else {
            const redirect = await fetchListingRedirect(apolloClient, path);

            if (redirect) {
                handleListingRedirect(res, redirect.uri, redirect.code);
                errorCode = redirect.code;
            }
        }

        if (res) {
            res.statusCode = errorCode;
        }
    }

    return { errorCode, listingId, data: data?.data };
};

export default getListingPageData;
